import React, { useMemo, useRef } from 'react';
import {
  Dimensions,
  FlatList,
  Modal,
  Platform,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { AdminColors } from '../constants/adminColors';
import FontFamily from '../../../constants/fonts';
import { useTheme } from '../../../theme/useTheme';
import type { SelectOption } from './AdminSelect';

interface AdminMultiSelectProps {
  values: string[];
  onChange: (values: string[]) => void;
  options: SelectOption[];
  allValue?: string;
  placeholder?: string;
}

const OPTION_HEIGHT = 42;
const LIST_MAX_HEIGHT = OPTION_HEIGHT * 4;
const SEARCH_BOX_HEIGHT = 42;
const MENU_APPROX_HEIGHT = SEARCH_BOX_HEIGHT + LIST_MAX_HEIGHT + 8;

type MenuPosition = { top?: number; bottom?: number; left: number; width: number };

const AdminMultiSelect: React.FC<AdminMultiSelectProps> = ({
  values,
  onChange,
  options,
  allValue = '',
  placeholder = 'Select...',
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [menuPos, setMenuPos] = React.useState<MenuPosition>({ top: 0, left: 0, width: 200 });
  const triggerRef = useRef<any>(null);

  const selectedValues = values ?? [allValue];

  const filteredOptions = useMemo(
    () => options.filter(o => o.label.toLowerCase().includes(search.toLowerCase())),
    [options, search],
  );

  const selectedDisplayText = useMemo(() => {
    const hasOnlyAll = selectedValues.length === 1 && selectedValues[0] === allValue;
    if (selectedValues.length === 0 || hasOnlyAll) {
      const allLabel = options.find(o => o.value === allValue)?.label;
      return allLabel || placeholder;
    }

    const selectedSet = new Set(selectedValues);
    const selectedLabels = options
      .filter(o => selectedSet.has(o.value) && o.value !== allValue)
      .map(o => o.label);

    return selectedLabels.length > 0 ? selectedLabels.join(', ') : placeholder;
  }, [allValue, options, placeholder, selectedValues]);

  const isSelected = (candidate: string) => selectedValues.includes(candidate);

  const toggle = () => {
    if (!open && triggerRef.current) {
      const screenH = Dimensions.get('window').height;
      if (Platform.OS === 'web') {
        try {
          const rect = (triggerRef.current as any).getBoundingClientRect();
          const spaceBelow = screenH - rect.bottom;

          if (spaceBelow < MENU_APPROX_HEIGHT + 8) {
            setMenuPos({ bottom: screenH - rect.top, left: rect.left, width: rect.width });
          } else {
            setMenuPos({ top: rect.bottom, left: rect.left, width: rect.width });
          }
        } catch (_) {
          // Ignore failed measurements and fallback to previous coordinates.
        }
      } else {
        triggerRef.current.measureInWindow?.((x: number, y: number, w: number, h: number) => {
          const spaceBelow = screenH - (y + h);
          if (spaceBelow < MENU_APPROX_HEIGHT + 8) {
            setMenuPos({ bottom: screenH - y, left: x, width: w });
          } else {
            setMenuPos({ top: y + h, left: x, width: w });
          }
        });
      }
    }

    setOpen(prev => !prev);
  };

  const toggleValue = (nextValue: string) => {
    const alreadySelected = selectedValues.includes(nextValue);
    let nextSelected = alreadySelected
      ? selectedValues.filter(v => v !== nextValue)
      : [...selectedValues, nextValue];

    // Keep an explicit "All" choice exclusive when represented by empty-string option.
    if (nextValue === allValue) {
      nextSelected = [allValue];
    } else {
      nextSelected = nextSelected.filter(v => v !== allValue);
    }

    if (nextSelected.length === 0) {
      nextSelected = [allValue];
    }

    onChange(nextSelected);
  };

  const close = () => {
    setOpen(false);
    setSearch('');
  };

  const resolvedPosition: any = Platform.OS === 'web'
    ? {
        position: 'fixed',
        left: menuPos.left,
        width: menuPos.width,
        ...(menuPos.bottom !== undefined ? { bottom: menuPos.bottom } : { top: menuPos.top }),
      }
    : {
        position: 'absolute',
        left: menuPos.left,
        width: menuPos.width,
        ...(menuPos.bottom !== undefined ? { bottom: menuPos.bottom } : { top: menuPos.top }),
      };

  return (
    <View>
      <TouchableOpacity
        ref={triggerRef}
        style={[styles.trigger, isDark && { backgroundColor: colors.input.background, borderColor: colors.input.border }]}
        onPress={toggle}
        activeOpacity={0.8}
      >
        <Text style={[styles.triggerText, isDark && { color: colors.text.primary }]} numberOfLines={1}>{selectedDisplayText}</Text>
        <MaterialIcons name={open ? 'arrow-drop-up' : 'arrow-drop-down'} size={20} color={isDark ? colors.text.secondary : AdminColors.gray500} />
      </TouchableOpacity>

      <Modal visible={open} transparent animationType="none" onRequestClose={close}>
        <TouchableOpacity style={styles.backdrop} activeOpacity={1} onPress={close} />
        <View style={[styles.menu, resolvedPosition, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }]}>
          <View style={[styles.searchBox, isDark && { borderBottomColor: colors.border.divider }]}>
            <MaterialIcons name="search" size={16} color={isDark ? colors.text.secondary : AdminColors.textMuted} style={styles.searchIcon} />
            <TextInput
              value={search}
              onChangeText={setSearch}
              placeholder="Search..."
              placeholderTextColor={isDark ? colors.text.disabled : AdminColors.gray200}
              style={[styles.searchInput, isDark && { color: colors.text.primary }]}
            />
          </View>

          <FlatList
            data={filteredOptions}
            keyExtractor={(item) => item.value || '__all__'}
            style={styles.list}
            scrollEnabled
            keyboardShouldPersistTaps="always"
            renderItem={({ item }) => {
              const selected = isSelected(item.value);
              return (
                <TouchableOpacity
                  style={[
                    styles.option,
                    selected && styles.optionSelected,
                    isDark && { borderBottomColor: colors.border.divider },
                    isDark && selected && { backgroundColor: 'rgba(61,139,255,0.15)' },
                  ]}
                  onPress={() => toggleValue(item.value)}
                >
                  <Text style={[styles.optionText, isDark && { color: colors.text.primary }]}>{item.label}</Text>
                  {selected && <MaterialIcons name="check" size={18} color={isDark ? colors.brand.primary : AdminColors.blue700} />}
                </TouchableOpacity>
              );
            }}
            ListEmptyComponent={
              <View style={styles.emptyState}>
                <Text style={[styles.emptyStateText, isDark && { color: colors.text.secondary }]}>No options found</Text>
              </View>
            }
          />
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  trigger: {
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 5,
    backgroundColor: AdminColors.bgSurface,
    minHeight: 36,
    paddingHorizontal: 10,
    paddingVertical: 6,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  triggerText: {
    flex: 1,
    fontSize: 13,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
    marginRight: 6,
  },
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  menu: {
    backgroundColor: AdminColors.bgSurface,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 8,
    zIndex: 9999,
  },
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.gray50,
  },
  searchIcon: {
    marginRight: 6,
  },
  searchInput: {
    flex: 1,
    fontSize: 13,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
    paddingVertical: 2,
  },
  list: {
    maxHeight: LIST_MAX_HEIGHT,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 14,
    height: OPTION_HEIGHT,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.blue25,
  },
  optionSelected: {
    backgroundColor: AdminColors.blue25,
  },
  optionText: {
    fontSize: 14,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
    flex: 1,
  },
  emptyState: {
    paddingVertical: 12,
    paddingHorizontal: 14,
  },
  emptyStateText: {
    fontSize: 13,
    color: AdminColors.textMuted,
    fontFamily: FontFamily.regular,
  },
});

export default AdminMultiSelect;
